<script setup lang="ts">
import {computed, ref} from 'vue';
import {Icon} from '@iconify/vue';
import {storeToRefs} from 'pinia';
import {useMusicUploadStore} from '@/stores/useMusicUploadStore';
import {useNotificationStore} from '@/stores/notifications';
import {useSettingsStore} from '@/stores/settings';
import {INFERRED_CHORD_COLOR} from '@/composables/musicXmlDom';
import {ChordInferenceBadge} from '@/components/chord-inference';

// 'compact' (Stepper): kompaktere Darstellung ohne den erklärenden Ergebnis-/Hinweisbereich.
const {variant = 'full'} = defineProps<{ variant?: 'full' | 'compact' }>();

const uploadStore = useMusicUploadStore();
const settingsStore = useSettingsStore();
const notificationStore = useNotificationStore();
const {currentFileId, isDetectingChords, isHavingFile, analysisResult, chordInferenceState, hasChordSymbols} = storeToRefs(uploadStore);

// Persistierte Settings (localStorage) über den Settings-Store; v-model-fähige computed-Felder.
// Regler der Folk-Erkennungs-Engine (siehe griffschrift.chords.folk_chord_detection).
const enabled = settingsStore.chordInferenceField('enabled');
const minChordConfidence = settingsStore.chordInferenceField('min_chord_confidence');
const nonChordToneTolerance = settingsStore.chordInferenceField('non_chord_tone_tolerance');
const mergeRepeated = settingsStore.chordInferenceField('merge_repeated_symbols');
const detectWithinMeasure = settingsStore.chordInferenceField('detect_changes_within_measure');
const restrictToBassLayout = settingsStore.chordInferenceField('restrict_to_bass_layout');
const keyOverride = settingsStore.chordInferenceField('key_override');
const followKeyChanges = settingsStore.chordInferenceField('follow_key_changes');

// Werte in music21-Schreibweise (Engine: folk_chord_detection.parse_key_name), Beschriftung deutsch.
const KEY_OPTIONS: { value: string; label: string }[] = [
  {value: 'C', label: 'C-Dur'}, {value: 'G', label: 'G-Dur'}, {value: 'D', label: 'D-Dur'},
  {value: 'A', label: 'A-Dur'}, {value: 'F', label: 'F-Dur'}, {value: 'B-', label: 'B-Dur'},
  {value: 'E-', label: 'Es-Dur'}, {value: 'Am', label: 'a-Moll'}, {value: 'Em', label: 'e-Moll'},
  {value: 'Dm', label: 'd-Moll'}, {value: 'Gm', label: 'g-Moll'}, {value: 'Cm', label: 'c-Moll'},
];

// Datei enthält bereits eigene Akkordsymbole -> Erkennung überschreibt diese nicht (Hinweis).
const hasOriginalChords = computed(() => analysisResult.value?.has_original_chord_symbols ?? false);

// Auch originale/vom User gesetzte Akkorde ersetzen. Bewusst NUR diese Erkennungs-Aktion und
// nicht persistiert (der Settings-Store trägt den Key nie): Convert-Requests senden damit
// default `overwrite_existing: false` – ein vergessener, klebriger Haken würde sonst bei einer
// späteren Datei still Original-Akkorde löschen.
const overwriteExisting = ref(false);

// Explizit per Button auslösen (kein Auto-Apply): wendet die aktuellen Settings auf den Inhalt an.
// Bei enabled=false entfernt der Aufruf die zuvor automatisch erkannten Akkorde wieder.
async function applyDetection() {
  if (!currentFileId.value) {
    notificationStore.notify('Bitte zunächst eine Datei hochladen.', 'warning', 2000);
    return;
  }

  const res = await uploadStore.applyChordInference({
    enabled: enabled.value,
    min_chord_confidence: minChordConfidence.value,
    non_chord_tone_tolerance: nonChordToneTolerance.value,
    merge_repeated_symbols: mergeRepeated.value,
    detect_changes_within_measure: detectWithinMeasure.value,
    restrict_to_bass_layout: restrictToBassLayout.value,
    key_override: keyOverride.value ?? null,
    follow_key_changes: followKeyChanges.value,
    overwrite_existing: overwriteExisting.value,
  });
  if (res) {
    notificationStore.notify(
        enabled.value ? 'Akkorde erkannt.' : 'Automatisch erkannte Akkorde entfernt.',
        'success', 2000,
    );
  }
}

// KEIN Mount-Reset von `enabled` (kritisch – nicht wieder einführen): `enabled` ist die
// persistierte Nutzer-Präferenz (steuert auch, ob der frische Upload das
// `chord_inference`-Formfeld mitschickt = Upload-Erkennung). Ein früheres
// `onMounted → enabled=false` bei compact setzte sie bei JEDEM Preview-Mount still zurück.
const isCompact = computed(() => variant === 'compact')
</script>

<template>
  <!-- Kompakte Variante (Vorschau-Toolbar) ausblenden, sobald die Datei ÜBERHAUPT Akkorde hat
       (eigene oder auto-erkannte): der Schnell-Umschalter ist dann überflüssig, Feinsteuerung bleibt
       über den Tab „Akkorderkennung" (Full). Full-Variante rendert immer. -->
  <div v-if="!(isCompact && hasChordSymbols)" :class="{'scale-0 max-h-0': !isHavingFile, 'animate-rotate-x scale-100':analysisResult}" class="animate-once max-w-full rounded-lg ">
  <!-- Status: laufende Erkennung ODER persistenter Anwendungsstatus (inhaltsbasiert, 5 Zustände). -->
  <div class="mb-2 flex justify-end">
    <div
        v-if="isDetectingChords"
        class="px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700 border border-purple-100 animate-fade-in flex items-center gap-1.5"
    >
      <Icon icon="fa6-solid:spinner" class="animate-spin"/>
      Erkenne Akkorde…
    </div>
    <ChordInferenceBadge v-else :status="chordInferenceState"/>
  </div>

  <!-- Ein/Aus -->
  <label
      class="group flex w-full items-center  rounded-xl  transition-all duration-200 cursor-pointer"
      :class="{'bg-sky-200/20 border-purple-400 shadow-sm' :enabled,
       'bg-white border-purple-600 border-1 hover:border-purple-400': !enabled,
       'p-2 gap-2 border-2': !isCompact,
  'p-0.5 gap-0.5 border-1': isCompact}"

  >
    <div
        class="flex items-center justify-center rounded-lg transition-colors"
        :class="{'bg-purple-500 text-white' :enabled,
       'bg-gray-100 text-gray-500': !enabled,
       ' w-10 h-10': !isCompact,
  ' w-8 h-8 text-purple-500/80 ': isCompact}"
    >
      <Icon icon="game-icons:piano-keys" class="text-lg text-purple-500"/>
    </div>
    <div class="flex-1">
      <div
          class="font-semibold text-gray-800 "
          :class="{
       ' text-sm': !isCompact,
  ' text-xs': isCompact}"
      >Automatische Akkorderkennung
      </div>
      <div
          class="text-gray-500 text-xs"
          :class="{

  'absolute opacity-0 hover:opacity-100 inline-flex w-[min(22.5rem,calc(100vw-2rem))] h-10 bottom-4 left-0 rounded-full border-gray-200 bg-slate-50/10 my-auto': isCompact}"
      >Akkorde aus den Tönen ableiten, wenn keine vorhanden sind
      </div>
    </div>
    <input v-model="enabled" @change="isCompact? applyDetection():undefined" type="checkbox" class="sr-only"/>
    <Icon
        :icon="enabled ? 'fa6-solid:toggle-on' : 'fa6-solid:toggle-off'"
        class="text-2xl"
        :class="enabled ? 'text-purple-500' : 'text-gray-300'"
    />
  </label>

  <!-- Folk-Regler nur wenn aktiv -->
  <div v-if="enabled && !isCompact" class="mt-3 space-y-3">
    <div>
      <label class="flex items-center justify-between text-xs font-semibold text-gray-600 mb-1">
        <span>Empfindlichkeit</span>
        <span class="text-purple-600 tabular-nums">{{ Math.round((minChordConfidence ?? 0.52) * 100) }}%</span>
      </label>
      <input
          v-model.number="minChordConfidence"
          type="range"
          min="0.3"
          max="0.9"
          step="0.01"
          class="w-full accent-purple-500"
      />
      <p class="mt-1 text-[11px] text-gray-500">
        Höher = nur sichere Akkorde (weniger Symbole); niedriger = mehr Akkorde.
      </p>
    </div>

    <div>
      <label class="flex items-center justify-between text-xs font-semibold text-gray-600 mb-1">
        <span>Durchgangston-Toleranz</span>
        <span class="text-purple-600 tabular-nums">{{ Math.round((nonChordToneTolerance ?? 0.3) * 100) }}%</span>
      </label>
      <input
          v-model.number="nonChordToneTolerance"
          type="range"
          min="0.1"
          max="0.6"
          step="0.05"
          class="w-full accent-purple-500"
      />
      <p class="mt-1 text-[11px] text-gray-500">
        Höher = mehr (auch schwächere) Töne fließen in die Akkordwahl ein.
      </p>
    </div>

    <!-- Nur bei Akkordwechsel ein Symbol setzen (Akkord-Dichte) -->
    <label
        class="flex w-full items-center gap-2 p-2 rounded-lg border transition-colors cursor-pointer"
        :class="mergeRepeated ? 'bg-purple-50 border-purple-300' : 'bg-white border-gray-200 hover:border-purple-200'"
    >
      <input v-model="mergeRepeated" type="checkbox" class="accent-purple-500"/>
      <div class="flex-1">
        <div class="text-xs font-semibold text-gray-700">Nur bei Akkordwechsel</div>
        <div class="text-[11px] text-gray-500">Ein Symbol nur setzen, wenn sich der Akkord ändert</div>
      </div>
    </label>

    <!-- Akkordwechsel innerhalb eines Takts (nur wenn der neue Akkord ganz klingt) -->
    <label
        class="flex w-full items-center gap-2 p-2 rounded-lg border transition-colors cursor-pointer"
        :class="detectWithinMeasure ? 'bg-purple-50 border-purple-300' : 'bg-white border-gray-200 hover:border-purple-200'"
    >
      <input v-model="detectWithinMeasure" type="checkbox" class="accent-purple-500"/>
      <div class="flex-1">
        <div class="text-xs font-semibold text-gray-700">Akkordwechsel im Takt</div>
        <div class="text-[11px] text-gray-500">Wechsel auf einem Schlag erkennen, wenn der neue Akkord vollständig klingt</div>
      </div>
    </label>

    <!-- Nur Akkorde, die das Bass-Layout (Harmonika-Tab) als Akkordknopf hat -->
    <label
        class="flex w-full items-center gap-2 p-2 rounded-lg border transition-colors cursor-pointer"
        :class="restrictToBassLayout ? 'bg-purple-50 border-purple-300' : 'bg-white border-gray-200 hover:border-purple-200'"
    >
      <input v-model="restrictToBassLayout" type="checkbox" class="accent-purple-500"/>
      <div class="flex-1">
        <div class="text-xs font-semibold text-gray-700">Nur am Bass greifbare Akkorde</div>
        <div class="text-[11px] text-gray-500">Nur Akkorde vorschlagen, für die dein Bass-Layout einen Akkordknopf hat</div>
      </div>
    </label>

    <!-- Tonart: automatisch (je Abschnitt) oder fest -->
    <div>
      <label for="chordInferenceKey" class="block text-xs font-semibold text-gray-600 mb-1">Tonart</label>
      <select
          id="chordInferenceKey"
          v-model="keyOverride"
          class="w-full rounded-lg border border-gray-200 bg-white p-1.5 text-xs text-gray-700"
      >
        <option :value="null">Automatisch erkennen</option>
        <option v-for="option in KEY_OPTIONS" :key="option.value" :value="option.value">{{ option.label }}</option>
      </select>
      <p class="mt-1 text-[11px] text-gray-500">
        Fest einstellen, wenn die erkannte Tonart nicht stimmt.
      </p>
    </div>

    <label
        v-if="!keyOverride"
        class="flex w-full items-center gap-2 p-2 rounded-lg border transition-colors cursor-pointer"
        :class="followKeyChanges ? 'bg-purple-50 border-purple-300' : 'bg-white border-gray-200 hover:border-purple-200'"
    >
      <input v-model="followKeyChanges" type="checkbox" class="accent-purple-500"/>
      <div class="flex-1">
        <div class="text-xs font-semibold text-gray-700">Tonartwechsel folgen</div>
        <div class="text-[11px] text-gray-500">Jeder Abschnitt mit neuer Vorzeichnung (z. B. Trio) bekommt seine eigene Tonart</div>
      </div>
    </label>

    <!-- Vorhandene (originale) Akkorde überschreiben -->
    <label
        v-if="!isCompact"
        class="flex w-full items-center gap-2 p-2 rounded-lg border transition-colors cursor-pointer"
        :class="overwriteExisting ? 'bg-amber-50 border-amber-600 border-amber-300' : 'bg-white border-gray-200 hover:border-amber-200'"
    >
      <input v-model="overwriteExisting" type="checkbox" class="accent-amber-500"/>
      <div class="flex-1">
        <div class="text-xs font-semibold text-gray-700">Vorhandene Akkorde überschreiben</div>
        <div class="text-[11px] text-gray-500">Auch eigene/originale Akkordsymbole ersetzen</div>
      </div>
    </label>
  </div>

  <!-- Anwenden-Button (explizit; kein Auto-Apply) -->
  <div class="flex gap-2 mt-4" v-if="enabled&&!isCompact">
    <button

        id="applyChordInference"
        class="btn btn-solid-purple-600 flex-1"
        type="button"
        :disabled="isDetectingChords || !currentFileId"
        @click="applyDetection"
    >
      <Icon v-if="isDetectingChords" icon="fa6-solid:spinner" class="animate-spin mr-2"/>
      <Icon v-else :icon="enabled ? 'fa6-solid:wand-magic-sparkles' : 'fa6-solid:eraser'" class="mr-2"/>
      {{ isDetectingChords ? 'Erkenne…' : (enabled ? 'Akkorde erkennen' : 'Auto-Akkorde entfernen') }}
    </button>
  </div>

  <!-- Hinweis: Datei hat bereits Akkorde -->
  <div
      v-if="'full' === variant && hasOriginalChords"
      class="mt-4 p-3 rounded-xl bg-amber-50 border border-amber-100 text-xs text-amber-800 flex items-start gap-2"
  >
    <Icon icon="fa6-solid:circle-info" class="mt-0.5 shrink-0"/>
    <span>
        Diese Datei enthält bereits Akkordsymbole. Vorhandene bleiben unangetastet; nur Takte
        ohne Symbol werden ergänzt (mit „Überschreiben" auch die vorhandenen).
      </span>
  </div>

  <!-- Hinweis auf Farbmarkierung -->
  <p v-if="'full' === variant && enabled && !hasOriginalChords" class="mt-3 text-[11px] text-gray-400 flex items-center gap-1.5">
    <span class="inline-block w-2.5 h-2.5 rounded-full" :style="{background: INFERRED_CHORD_COLOR}"></span>
    Automatisch erkannte Akkorde sind eingefärbt.
  </p>
  </div>
</template>
